// wijmo
import * as wjcCore from "@grapecity/wijmo"
import * as wjcInput from "@grapecity/wijmo.input"
import { ComboBox } from "@grapecity/wijmo.react.input"
import React, { useEffect, useState } from "react"
import { TWijmoComboBox } from "../../type/wijmoComponents"

/**
 * BaseComboBox拡張側プロパティインタフェースタイプ
 */
export type TExtendedBaseComboBoxProps<TValue, TItem> = {
  isLoadEnded: boolean
  setIsLoadEnded: React.Dispatch<React.SetStateAction<boolean>>
  ComboBox: TWijmoComboBox<TValue, TItem> | null
  setComboBox: (
    value: React.SetStateAction<TWijmoComboBox<TValue, TItem> | null>
  ) => void
  itemsSource?: TItem[]
  autoFocus?: boolean
  className?: string
  id?: string
  isAllSelect?: boolean
  isRequired?: boolean
  isEmpty?: boolean
  gotFocus?: (component: TWijmoComboBox<TValue, TItem>) => unknown
  lostFocus?: (component: TWijmoComboBox<TValue, TItem>) => unknown
  isDisabled?: boolean
  isReadOnly?: boolean
  isUnselectStart?: boolean
  isInlineBlockComponent?: boolean
  isEditable?: boolean
  text?: string | null
  defaultValue?: TValue | null
  displayTextFormatter?: (item: TItem) => string
  setText?: (text: string | null) => void
  selectedItem?: TItem | null
  setSelectedItem?: (item: TItem | null) => void
  selectedValue?: TValue | null
  setSelectedValue?: (value: TValue | null) => void
  placeholder?: string
  tabIndex?: number
  listFilter?: (item: TItem) => boolean
  loadedItemsFilter?: (item: TItem) => boolean
  initialized?: (component: TWijmoComboBox<TValue, TItem>) => void
  itemsSourceChanged?: (
    component: TWijmoComboBox<TValue, TItem>,
    e: wjcCore.EventArgs
  ) => void
  refreshed?: (
    component: TWijmoComboBox<TValue, TItem>,
    e: wjcCore.EventArgs
  ) => void
  onInvalidInput?: (
    component: TWijmoComboBox<TValue, TItem>,
    e: wjcCore.CancelEventArgs
  ) => void
  formatItem?: (
    control: wjcInput.ComboBox,
    e: wjcInput.FormatItemEventArgs
  ) => unknown
}

/**
 * 基底BaseComboBoxプロパティインタフェースタイプ
 */
type TBaseComboBoxProps<TValue, TItem> = TExtendedBaseComboBoxProps<
  TValue,
  TItem
> & {
  displayField: keyof TItem | "name" | "innerName"
  valueField: string | "value"
}

/**
 * マスタの項目を選択項目にするコンボボックスのコンポーネント
 * @param props
 * @returns
 */
const BaseComboBox: <TValue, TItem>(
  props: TBaseComboBoxProps<TValue, TItem>
) => React.ReactElement<TBaseComboBoxProps<TValue, TItem>> = (props) => {
  const [classNameInner, setClassNameInner] = useState("")

  //#region  内部ハンドラ定義

  /**
   * 初期化時イベントハンドラ
   */
  const initializedInnerHandler = (control: wjcInput.ComboBox) => {
    // isRequired時、deleteを押された際は先頭を選択する(「すべて」があれば「すべて」となる)
    const inputElement = control.inputElement as HTMLInputElement
    inputElement.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (control.isDisabled) return
        if (control.isReadOnly) return

        if (!control.isRequired) return
        if (!e.altKey && !e.ctrlKey && !e.shiftKey) {
          if (e.key === "Delete") {
            control.selectedIndex = 0
            e.preventDefault()
            e.stopImmediatePropagation()
          }
        }
      },
      true
    )
    props.initialized && props.initialized(control)

    const newControl: wjcInput.ComboBox & { isLoadEnded?: boolean } = control
    newControl.isLoadEnded = false
    props.setComboBox != null && props.setComboBox(newControl)
  }

  /**
   * itemsSource変更完了時イベントハンドラ
   * @param component
   * @param e
   */
  const itemsSourceChangedInnerHandler = (
    control: wjcInput.ComboBox,
    e: wjcCore.EventArgs
  ) => {
    if (props.isUnselectStart && !props.isAllSelect) control.selectedItem = null

    props.itemsSourceChanged && props.itemsSourceChanged(control, e)
  }

  /**
   * 再描画完了時イベントハンドラ
   * @param component
   * @param e
   */
  const refreshedInnerHandler = (
    control: wjcInput.ComboBox,
    e: wjcCore.EventArgs
  ) => {
    // autoFocus対応
    if (props.autoFocus) control.inputElement?.focus()
    props.refreshed && props.refreshed(control, e)
  }

  //#endregion

  // placeHolderとfloatinglabel用
  const [innerText, setInnerText] = useState<string>()
  const [isShowFloatingLabel, setIsShowFloatingLabel] = useState<boolean>()

  useEffect(() => {
    if (props.placeholder && innerText) {
      setIsShowFloatingLabel(true)
    } else {
      setIsShowFloatingLabel(false)
    }
  }, [props.placeholder, innerText])

  // classあて
  useEffect(() => {
    const classNameArray: string[] = []
    props.isRequired && classNameArray.push("required_input")
    props.isEmpty && classNameArray.push("has-empty-input")
    props.className && classNameArray.push(props.className)
    if (classNameArray.length > 0) {
      setClassNameInner(classNameArray.join(" "))
    } else {
      setClassNameInner("")
    }
  }, [props.isRequired, props.className, props.isEmpty])

  return (
    <div
      className={
        props.isInlineBlockComponent
          ? "floating-frame d-inline-block"
          : "floating-frame"
      }>
      {isShowFloatingLabel && (
        <span className="floating-label">{props.placeholder}</span>
      )}
      <ComboBox
        id={props.id}
        lostFocus={props.lostFocus}
        isEditable={props.isEditable ?? false}
        isRequired={props.isRequired}
        isDisabled={props.isDisabled}
        isReadOnly={props.isReadOnly}
        headerPath={null}
        className={classNameInner}
        displayMemberPath={props.displayField}
        selectedValuePath={props.valueField}
        initialized={initializedInnerHandler}
        itemsSourceChanged={itemsSourceChangedInnerHandler}
        refreshed={refreshedInnerHandler}
        text={props.text}
        gotFocus={props.gotFocus}
        textChanged={(control: wjcInput.ComboBox, e: wjcCore.EventArgs) => {
          setInnerText(control.text ?? "")
          props.setText && props.setText(control.text)
        }}
        selectedIndexChanged={(
          control: wjcInput.ComboBox,
          e: wjcCore.EventArgs
        ) => {
          props.setSelectedItem && props.setSelectedItem(control.selectedItem)
          props.setSelectedValue &&
            props.setSelectedValue(control.selectedValue)
        }}
        selectedValue={props.selectedValue}
        selectedItem={props.selectedItem}
        placeholder={isShowFloatingLabel ? "" : props.placeholder ?? ""}
        itemsSource={props.itemsSource}
        //wjItemTemplate={props.wjItemTemplate} // 初期表示時に期待通りの動作をしないため使用禁止
        invalidInput={props.onInvalidInput}
        tabOrder={props.tabIndex}
        formatItem={props.formatItem}></ComboBox>
    </div>
  )
}
export default BaseComboBox
